import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { isHideMetrics, isShopifyPreview, isTestUser } from '@/modules/api';
import { PreloadAttachmentsResponse } from '@/modules/api/types';
import { ProductCard } from '@/modules/common';
import { LinkType } from '@/modules/models';
import { bridgeAtom, messagesReceivedTimeAtom, renderedMessagesAtom, useAtom, useValue } from '@/store';
import { getRelativeUrl, msToSeconds, sanitizeImageUrl } from '@/utils';
import { MessageTextWrapper, ProcessingTime, ProcessingTimeWrapper } from './MessageText.styles';

interface BubbleProps {
  id?: number;
  links: LinkType[] | null;
  attachments?: PreloadAttachmentsResponse | null;
  text: string;
  isUserMessage: boolean;
  isSystemMessage: boolean;
  isLiveAgentMessage?: boolean;
  showAgentNameTag?: boolean;
  direction?: 'ltr' | 'rtl';
  mlBeProcessingTime: number | null;
}

export const MessageText = memo<BubbleProps>((props) => {
  const {
    id,
    mlBeProcessingTime,
    links,
    isUserMessage,
    isSystemMessage,
    isLiveAgentMessage,
    showAgentNameTag,
    text: messageText,
    direction = 'ltr',
    attachments,
  } = props;
  const text = messageText ?? '';

  const isTest = isTestUser();
  const isPreview = isShopifyPreview();
  const isMetricsHidden = isHideMetrics();

  const [renderedMessages, setRenderedMessages] = useAtom(renderedMessagesAtom);

  const messagesReceivedTime = useValue(messagesReceivedTimeAtom);
  const { productCard } = useValue(bridgeAtom);

  const [isSingleLine, setIsSingleLine] = useState(false);
  const [productTargets, setProductTargets] = useState<{ element: HTMLElement; handle: string }[]>([]);
  const [renderTime, setRenderTime] = useState(0);

  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const imageLinks = links?.filter(({ type }) => type === 'image') || [];
  const imageAttachments = attachments?.filter(({ type }) => type === 'image') || [];

  useEffect(() => {
    const checkMultiline = () => {
      if (!bubbleRef.current) {
        return;
      }

      const computedStyle = window.getComputedStyle(bubbleRef.current);
      const paddingTop = parseFloat(computedStyle.paddingTop);
      const paddingBottom = parseFloat(computedStyle.paddingBottom);
      const lineHeight = parseFloat(computedStyle.lineHeight);
      const contentHeight = bubbleRef.current.clientHeight - paddingTop - paddingBottom;

      setIsSingleLine(contentHeight <= lineHeight * 2.5);
    };

    checkMultiline();
    window.addEventListener('resize', checkMultiline);

    return () => {
      window.removeEventListener('resize', checkMultiline);
    };
  }, []);

  const shouldBreakWords = text.split(' ').length === 1;

  const makeAllUrlsRelative = (input: string) => {
    const urlRegex = /https?:\/\/[^\s"'<>]+/g;

    return input.replace(urlRegex, (url) => {
      return getRelativeUrl(url);
    });
  };

  const messageContent = productCard?.relativeLinks ? makeAllUrlsRelative(text) : text;
  const formattedText = parse(DOMPurify.sanitize(messageContent, { USE_PROFILES: { html: true } }));
  const hasProductRecommendationsList = messageContent.includes('product-recommendations-list');

  const getProductFromLinks = useCallback(
    (searchHandle: string) => links?.find(({ handle }) => searchHandle === handle),
    [links]
  );

  useEffect(() => {
    if (bubbleRef.current) {
      const nodes = bubbleRef.current.querySelectorAll('.product');
      const newTargets: { element: HTMLElement; handle: string }[] = [];

      if (!nodes.length && id && !renderedMessages[id]) {
        setRenderedMessages((prev) => ({
          ...prev,
          [id]: true,
        }));
        return;
      }

      nodes.forEach((el) => {
        if (el instanceof HTMLElement) {
          const handle = el.dataset.handle;
          if (handle) {
            newTargets.push({ element: el, handle });
          }
        }
      });

      const isEqual =
        productTargets.length === newTargets.length &&
        productTargets.every((prev, i) => prev.handle === newTargets[i].handle);

      if (!isEqual) {
        // oxlint-disable-next-line react/set-state-in-effect -- synchronizes with DOM nodes rendered by parse(), not derivable during render
        setProductTargets(newTargets);
      }
    }
  }, [productTargets, formattedText, setRenderedMessages, id, renderedMessages]);

  useEffect(() => {
    if (!id || renderTime) {
      return;
    }

    const isRendered = Boolean(renderedMessages?.[id]);
    if (isRendered) {
      // oxlint-disable-next-line react/set-state-in-effect -- measures actual paint timing, not derivable during render
      setRenderTime(new Date().getTime() - messagesReceivedTime?.[id]);
    }
  }, [renderTime, renderedMessages, messagesReceivedTime, id]);

  const isProcessingMetricsVisible =
    isTest && !isPreview && !isUserMessage && !isSystemMessage && !isLiveAgentMessage && !isMetricsHidden;
  const isLastSystemMessage = isSystemMessage && text.includes('has ended');

  return (
    <MessageTextWrapper
      ref={bubbleRef}
      className={hasProductRecommendationsList ? 'products-message' : undefined}
      isUserMessage={isUserMessage}
      isSystemMessage={isSystemMessage}
      isLastSystemMessage={isLastSystemMessage}
      isLiveAgentMessage={isLiveAgentMessage}
      showAgentNameTag={showAgentNameTag}
      shouldBreakWords={shouldBreakWords}
      isSingleLine={isSingleLine}
      direction={direction}
    >
      {formattedText}
      {productTargets.map(({ element, handle }) => {
        const product = getProductFromLinks(handle);

        if (!product) {
          return null;
        }

        return ReactDOM.createPortal(<ProductCard messageId={id} product={product} bubbleRef={bubbleRef} />, element);
      })}
      {imageLinks
        .filter(({ imageUrl }) => Boolean(imageUrl))
        .map(({ imageUrl }, index) => (
          <img
            key={index}
            src={sanitizeImageUrl(imageUrl!)}
            alt={`Attached image ${index + 1}`}
            className="link-image"
          />
        ))}
      {imageAttachments
        .filter(({ url }) => Boolean(url))
        .map(({ url }, index) => (
          <img key={index} src={sanitizeImageUrl(url)} alt={`Attached image ${index + 1}`} className="link-image" />
        ))}
      {isProcessingMetricsVisible && (
        <ProcessingTimeWrapper id="processing-time-wrapper">
          {mlBeProcessingTime && <ProcessingTime>ML & BE: {msToSeconds(mlBeProcessingTime)}s</ProcessingTime>}
          {Boolean(renderTime) && <ProcessingTime>FE: {msToSeconds(renderTime)}s</ProcessingTime>}
        </ProcessingTimeWrapper>
      )}
    </MessageTextWrapper>
  );
});

MessageText.displayName = 'MessageText';
